import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fm } from '../utils';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_API_URL || '/api';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="group bg-white dark:bg-surface-800 rounded-2xl p-6 border border-surface-100 dark:border-surface-700 shadow-soft hover:shadow-soft-lg transition-all duration-300 hover:-translate-y-0.5">
    <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center text-xl mb-4 group-hover:scale-110 transition-transform duration-300">{icon}</div>
    <h4 className="font-display font-semibold text-surface-800 dark:text-white text-sm mb-1.5">{title}</h4>
    <p className="text-xs text-surface-400 dark:text-surface-500 leading-relaxed">{desc}</p>
  </div>
);

const CampaignCard = ({ item }) => (
  <div className="group relative bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl overflow-hidden shadow-soft-lg min-h-[200px]">
    {item.image && <img src={item.image} alt={item.title} className="absolute inset-0 w-full h-full object-cover opacity-40" />}
    <div className="relative p-6 flex flex-col justify-end min-h-[200px]">
      {item.discountRate > 0 && (
        <div className="absolute top-3 right-3 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-lg">%{item.discountRate} İNDİRİM</div>
      )}
      <h3 className="font-display font-bold text-white text-lg mb-1">{item.title || 'Kampanya'}</h3>
      <p className="text-white/80 text-xs leading-relaxed">{item.description || 'Kampanya detayları yakında...'}</p>
    </div>
  </div>
);

const EmptyState = ({ icon, title, desc }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <div className="w-16 h-16 bg-surface-100 dark:bg-surface-700/50 rounded-2xl flex items-center justify-center text-3xl mb-4">{icon || '📋'}</div>
    <h3 className="font-display font-semibold text-surface-500 dark:text-surface-400 text-sm mb-1">{title || 'İçerik henüz eklenmedi'}</h3>
    <p className="text-xs text-surface-400 dark:text-surface-500 text-center max-w-sm">{desc || 'Admin panelinden bu alanı düzenleyebilirsiniz.'}</p>
  </div>
);

const SliderDots = ({ total, active, onChange }) => (
  <div className="flex gap-1.5 justify-center mt-4">
    {Array.from({ length: total }).map((_, i) => (
      <button key={i} onClick={() => onChange(i)}
        className={`w-2 h-2 rounded-full transition-all duration-300 ${i === active ? 'w-6 bg-emerald-500' : 'bg-surface-300 dark:bg-surface-600'}`} />
    ))}
  </div>
);

const LandingPage = () => {
  const [kategoriler, setKategoriler] = useState([]);
  const [urunlerMap, setUrunlerMap] = useState({});
  const [acikKategori, setAcikKategori] = useState(null);
  const [settings, setSettings] = useState(null);
  const [sliders, setSliders] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [socials, setSocials] = useState([]);
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sliderIdx, setSliderIdx] = useState(0);
  const { isAuthenticated, kullanici, logout } = useAuth();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [k, s, sl, c, so, h] = await Promise.all([
        fetch(`${API}/kategoriler`),
        fetch(`${API}/sitesettings`),
        fetch(`${API}/homesliders`),
        fetch(`${API}/campaigns`),
        fetch(`${API}/socialmedia`),
        fetch(`${API}/workinghours`),
      ]);
      const [kData, sData, slData, cData, soData, hData] = await Promise.all([
        k.json(), s.json(), sl.json(), c.json(), so.json(), h.json()
      ]);
      setKategoriler(kData);
      setSettings(sData);
      setSliders(slData);
      setCampaigns(cData);
      setSocials(soData);
      setHours(hData);
    } catch (e) { console.error('Yükleme hatası:', e); }
    finally { setLoading(false); }
  };

  const kategoriToggle = async (kategoriId) => {
    if (acikKategori === kategoriId) { setAcikKategori(null); return; }
    setAcikKategori(kategoriId);
    if (!urunlerMap[kategoriId]) {
      try { const r = await fetch(`${API}/urunler/kategori/${kategoriId}`); const data = await r.json(); setUrunlerMap(p => ({ ...p, [kategoriId]: data })); }
      catch (e) { console.error('Ürünler yüklenemedi:', e); }
    }
  };

  useEffect(() => { if (sliders.length > 1) { const iv = setInterval(() => setSliderIdx(i => (i + 1) % sliders.length), 5000); return () => clearInterval(iv); } }, [sliders.length]);

  const s = settings || {};
  const getPanelLink = () => { if (!kullanici) return '/garson'; return { Admin: '/admin', Garson: '/garson', Kasa: '/kasa', Mutfak: '/mutfak' }[kullanici.rol] || '/garson'; };

  if (loading) return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-surface-400 font-medium">Yükleniyor...</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900">
      {/* NAVBAR */}
      <nav className="sticky top-0 z-30 bg-white/80 dark:bg-surface-800/80 backdrop-blur-xl border-b border-surface-100 dark:border-surface-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              {s.logo ? (
                <img src={s.logo} alt={s.siteName} className="h-9 w-auto" />
              ) : (
                <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
              )}
              <span className="font-display font-bold text-surface-800 dark:text-white">{s.siteName || 'NEŞVE Cafe'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/menu"
                className="px-4 py-2 text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-xl transition-all duration-200 shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30">
                {s.ctaText || 'Menüyü Görüntüle'}
              </Link>
              {isAuthenticated ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-surface-400 dark:text-surface-500 hidden sm:block">{kullanici?.adSoyad}</span>
                  <Link to={getPanelLink()}
                    className="px-4 py-2 text-xs font-medium text-surface-600 dark:text-surface-300 bg-surface-100 dark:bg-surface-700 rounded-xl hover:bg-surface-200 dark:hover:bg-surface-600 transition-all">
                    Panele Git
                  </Link>
                  <button onClick={logout}
                    className="px-3 py-2 text-xs font-medium text-red-500 hover:text-red-600 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-all">
                    Çıkış
                  </button>
                </div>
              ) : (
                <Link to="/login"
                  className="px-4 py-2 text-xs font-medium text-surface-500 dark:text-surface-400 bg-surface-100 dark:bg-surface-700 rounded-xl hover:bg-surface-200 dark:hover:bg-surface-600 transition-all">
                  Personel Girişi
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* HERO / SLIDER */}
      {sliders.length > 0 ? (
        <section className="relative overflow-hidden h-[320px] sm:h-[400px] lg:h-[480px] bg-surface-900">
          <AnimatePresence mode="wait">
            <motion.div
              key={sliderIdx}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              <div className="relative h-full w-full">
                {/* Background Image with Ken Burns Effect */}
                <motion.div
                  initial={{ scale: 1.15, opacity: 0 }}
                  animate={{ scale: 1, opacity: 0.4 }}
                  transition={{ duration: 6, ease: "linear" }}
                  className="absolute inset-0 w-full h-full"
                >
                  {sliders[sliderIdx].image && (
                    <img
                      src={sliders[sliderIdx].image}
                      alt={sliders[sliderIdx].title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </motion.div>

                {/* Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-tr from-surface-900 via-surface-900/60 to-transparent" />

                {/* Content */}
                <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                  <div className="max-w-3xl">
                    <motion.h1
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
                      className="font-display font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.2] mb-4 drop-shadow-2xl"
                    >
                      {sliders[sliderIdx].title || 'Hoş Geldiniz'}
                    </motion.h1>
                    
                    <motion.p
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.6, duration: 0.8, ease: "easeOut" }}
                      className="text-white/80 text-base sm:text-lg max-w-xl leading-relaxed mb-6 font-medium"
                    >
                      {sliders[sliderIdx].description || 'Size en iyi deneyimi sunmak için buradayız.'}
                    </motion.p>

                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.8, duration: 0.8, ease: "easeOut" }}
                    >
                      <Link to="/menu"
                        className="inline-flex items-center gap-2.5 px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-display font-bold rounded-xl shadow-2xl shadow-emerald-500/20 hover:scale-105 transition-all duration-300 text-sm group"
                      >
                        {s.ctaText || 'Menüyü Keşfet'}
                        <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Controls Overlay */}
          <div className="absolute bottom-8 left-0 right-0 z-20">
            <SliderDots total={sliders.length} active={sliderIdx} onChange={setSliderIdx} />
          </div>

          {/* Bottom Curve Decor */}
          <div className="absolute -bottom-1 left-0 right-0 h-16 bg-surface-50 dark:bg-surface-900 z-20" 
            style={{ clipPath: 'ellipse(60% 100% at 50% 100%)' }} />
        </section>
      ) : (
        <section className="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
            <div className="max-w-2xl">
              <h1 className="font-display font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight mb-3">
                {s.heroTitle || 'Her Anın Tadı Güzel Olsun'}
              </h1>
              <p className="text-white/80 text-base sm:text-lg max-w-lg leading-relaxed mb-6">
                {s.heroSubtitle || 'En kaliteli malzemelerle hazırlanan lezzetlerimizle sizlere unutulmaz bir deneyim sunuyoruz.'}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to={s.ctaLink || '/menu'}
                  className="px-8 py-3 bg-white text-emerald-700 font-display font-bold rounded-xl shadow-2xl hover:shadow-emerald-500/30 hover:scale-105 transition-all duration-200 text-sm">
                  {s.ctaText || 'Menüyü Keşfet'}
                </Link>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-1 left-0 right-0 h-16 bg-surface-50 dark:bg-surface-900" style={{ clipPath: 'ellipse(55% 100% at 50% 100%)' }} />
        </section>
      )}

      {/* KAMPANYALAR */}
      {campaigns.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h2 className="font-display font-bold text-surface-800 dark:text-white text-xl mb-6">Kampanyalar</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {campaigns.map((c, i) => <CampaignCard key={i} item={c} />)}
          </div>
        </section>
      )}

      {/* KATEGORİLER (AKORDİYON) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 relative z-10">
        {kategoriler.length > 0 ? (
          <div className="bg-white dark:bg-surface-800 rounded-3xl p-6 sm:p-8 shadow-soft-lg border border-surface-100 dark:border-surface-700">
            <h2 className="font-display font-bold text-surface-800 dark:text-white text-lg mb-5">Kategorilerimiz</h2>
            <div className="space-y-3">
              {kategoriler.map(kat => {
                const acik = acikKategori === kat.kategoriID;
                return (
                  <div key={kat.kategoriID} className="border border-surface-100 dark:border-surface-700 rounded-2xl overflow-hidden transition-all duration-200">
                    <button onClick={() => kategoriToggle(kat.kategoriID)}
                      className={`w-full flex items-center gap-3 p-4 text-left transition-colors ${
                        acik ? 'bg-emerald-50 dark:bg-emerald-900/10' : 'hover:bg-surface-50 dark:hover:bg-surface-700/30'
                      }`}>
                      <div className="text-2xl flex-shrink-0">{['☕', '🍰', '🥪', '🥤', '🍳', '🍕', '🍝', '🥗'][kat.kategoriID % 8] || '🍽️'}</div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-semibold text-sm ${acik ? 'text-emerald-700 dark:text-emerald-300' : 'text-surface-800 dark:text-white'}`}>
                          {kat.kategoriAdi}
                        </h3>
                        {kat.aciklama && <p className="text-[10px] text-surface-400 dark:text-surface-500 mt-0.5 line-clamp-1">{kat.aciklama}</p>}
                      </div>
                      <svg className={`w-5 h-5 text-surface-400 flex-shrink-0 transition-transform duration-200 ${acik ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {acik && (
                      <div className="border-t border-surface-100 dark:border-surface-700 p-4 bg-surface-50/50 dark:bg-surface-700/10">
                        {urunlerMap[kat.kategoriID] ? (
                          urunlerMap[kat.kategoriID].length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                              {urunlerMap[kat.kategoriID].map((urun, idx) => (
                                <div key={urun.urunID} className="bg-white dark:bg-surface-800 rounded-xl overflow-hidden border border-surface-100 dark:border-surface-700 shadow-soft"
                                  style={{ animation: `fadeInUp 0.3s ease-out ${idx * 0.04}s both` }}>
                                  <div className="aspect-[4/3] bg-surface-100 dark:bg-surface-700 overflow-hidden">
                                    {urun.resimBase64 ? (
                                      <img src={urun.resimBase64} alt={urun.urunAdi} className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <div className="text-3xl">{['☕', '🍰', '🥪', '🥤', '🍳', '🍕', '🍝', '🥗'][kat.kategoriID % 8] || '🍽️'}</div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="p-3">
                                    <h4 className="font-semibold text-surface-800 dark:text-white text-xs mb-0.5">{urun.urunAdi}</h4>
                                    <p className="font-display font-bold text-emerald-600 dark:text-emerald-400 text-sm">{fm(urun.fiyat)}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-surface-400 text-center py-6">Bu kategoride ürün bulunmuyor</p>
                          )
                        ) : (
                          <div className="flex items-center justify-center py-6">
                            <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <EmptyState icon="📂" title="Kategoriler yakında" desc="Menü kategorileri henüz eklenmemiş." />
        )}
      </section>

      {/* HAKKIMIZDA */}
      {(s.aboutTitle || s.aboutDescription) && (
        <section className="bg-white dark:bg-surface-800 border-t border-surface-100 dark:border-surface-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-display font-bold text-surface-800 dark:text-white text-2xl mb-4">{s.aboutTitle || 'Hakkımızda'}</h2>
                <p className="text-sm text-surface-500 dark:text-surface-400 leading-relaxed whitespace-pre-line">{s.aboutDescription}</p>
              </div>
              {s.aboutImage && (
                <div className="rounded-2xl overflow-hidden shadow-soft-lg">
                  <img src={s.aboutImage} alt={s.aboutTitle} className="w-full h-80 object-cover" />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ÖZELLİKLER */}
      <section className="bg-surface-50/50 dark:bg-surface-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center mb-10">
            <h2 className="font-display font-bold text-surface-800 dark:text-white text-2xl mb-2">Neden Biz?</h2>
            <p className="text-sm text-surface-400 dark:text-surface-500 max-w-lg mx-auto">Kalite ve lezzeti bir arada sunuyoruz</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <FeatureCard icon="🌿" title="Taze Malzeme" desc="Günlük tedarik edilen taze malzemeler" />
            <FeatureCard icon="👨‍🍳" title="Usta Şefler" desc="Deneyimli ve profesyonel ekip" />
            <FeatureCard icon="📱" title="Dijital Menü" desc="Masanızdaki QR ile kolay sipariş" />
            <FeatureCard icon="⏱️" title="Hızlı Servis" desc="Minimum bekleme süresi" />
          </div>
        </div>
      </section>

      {/* İLETİŞİM */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="bg-gradient-to-br from-surface-50 to-white dark:from-surface-800 dark:to-surface-800/50 rounded-3xl p-6 sm:p-8 lg:p-10 border border-surface-100 dark:border-surface-700 shadow-soft">
          <h2 className="font-display font-bold text-surface-800 dark:text-white text-xl mb-8">İletişim</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-5">
              {[
                { icon: '📍', title: 'Adres', value: s.address },
                { icon: '📞', title: 'Telefon', value: s.phone },
                { icon: '✉️', title: 'E-posta', value: s.email },
              ].map((item, i) => item.value ? (
                <div key={i} className="flex items-start gap-3.5">
                  <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center flex-shrink-0 text-lg">{item.icon}</div>
                  <div>
                    <h4 className="font-semibold text-surface-700 dark:text-surface-300 text-xs mb-0.5">{item.title}</h4>
                    <p className="text-sm text-surface-600 dark:text-surface-400">{item.value}</p>
                  </div>
                </div>
              ) : null)}

              {/* ÇALIŞMA SAATLERİ */}
              {hours.length > 0 && (
                <div className="flex items-start gap-3.5">
                  <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center flex-shrink-0 text-lg">🕐</div>
                  <div>
                    <h4 className="font-semibold text-surface-700 dark:text-surface-300 text-xs mb-1">Çalışma Saatleri</h4>
                    <div className="space-y-0.5">
                      {hours.map((h, i) => (
                        <p key={i} className="text-[11px] text-surface-500 dark:text-surface-400">
                          <span className="font-medium text-surface-600 dark:text-surface-300">{h.day}:</span>
                          {' '}{h.isClosed ? 'Kapalı' : `${h.openingTime || '?'} - ${h.closingTime || '?'}`}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="rounded-2xl overflow-hidden bg-surface-100 dark:bg-surface-700 min-h-[250px]">
              {(() => {
                let mapUrl = '';
                if (s.googleMapsLink) {
                  if (s.googleMapsLink.includes('embed') || s.googleMapsLink.includes('output=embed')) {
                    mapUrl = s.googleMapsLink;
                  } else if (s.googleMapsLink.includes('maps/place/')) {
                    const parts = s.googleMapsLink.split('maps/place/');
                    const addressPart = parts[1]?.split('/')[0];
                    if (addressPart) mapUrl = `https://maps.google.com/maps?q=${addressPart}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
                  } else {
                    mapUrl = `https://maps.google.com/maps?q=${encodeURIComponent(s.googleMapsLink)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
                  }
                } else if (s.address) {
                  mapUrl = `https://maps.google.com/maps?q=${encodeURIComponent(s.address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
                }

                if (mapUrl) {
                  return <iframe title="Konum" src={mapUrl} className="w-full h-full min-h-[350px]" style={{ border: 0 }} allowFullScreen loading="lazy" />;
                }

                return (
                  <div className="flex items-center justify-center h-full min-h-[350px]">
                    <div className="text-center p-6">
                      <div className="text-4xl mb-3">🗺️</div>
                      <p className="text-sm text-surface-400 dark:text-surface-500">Harita bulunamadı</p>
                      <p className="text-[10px] text-surface-300 dark:text-surface-600 mt-1">Admin panelinden adres ekleyin</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-surface-100 dark:border-surface-700/30 bg-white/50 dark:bg-surface-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {s.logo ? <img src={s.logo} alt={s.siteName} className="h-7 w-auto" /> : null}
                <span className="font-display font-bold text-surface-800 dark:text-white text-sm">{s.siteName || 'NEŞVE Cafe'}</span>
              </div>
              <p className="text-[11px] text-surface-400 dark:text-surface-500 leading-relaxed">{s.footerDescription || s.description || ''}</p>
            </div>
            <div>
              <h5 className="font-semibold text-surface-700 dark:text-surface-300 text-xs mb-2">Takip Et</h5>
              <div className="flex flex-wrap gap-2">
                {socials.map((sm, i) => (
                  <a key={i} href={sm.url} target="_blank" rel="noopener noreferrer"
                    className="px-3 py-1.5 text-[10px] font-medium bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:text-emerald-600 dark:hover:text-emerald-400 transition-all">
                    {sm.platform}
                  </a>
                ))}
              </div>
            </div>
            <div>
              <h5 className="font-semibold text-surface-700 dark:text-surface-300 text-xs mb-2">İletişim</h5>
              <p className="text-[11px] text-surface-400 dark:text-surface-500">{s.phone && `📞 ${s.phone}`}</p>
              <p className="text-[11px] text-surface-400 dark:text-surface-500">{s.email && `✉️ ${s.email}`}</p>
            </div>
          </div>
          <div className="border-t border-surface-100 dark:border-surface-700/50 pt-4 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-[10px] text-surface-400 dark:text-surface-500">© 2026 <a href="https://www.zirvetekyazilim.com.tr" target="_blank" rel="noopener noreferrer" className="font-semibold text-emerald-500 hover:text-emerald-600 transition-colors">ZİRVETEK Yazılım Danışmanlık</a>. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
